// Áp thương hiệu NPP theo người đang dùng (docs/KE_HOACH_THUONG_HIEU_NPP.md):
//   · Khu Quản trị NPP (/npp…) → NPP của tài khoản quản trị.
//   · Đơn vị (Shop / CTV / Đại lý) → NPP của đơn vị đang mở (GET /settings/shop trả kèm brand).
//   · Các màn đăng nhập (Đăng nhập, Đăng ký, Quên mật khẩu…) → luôn ShopTrack: NPP không đổi giao diện đăng nhập.
//   · Chưa đăng nhập ở màn khác (đang khôi phục phiên khi tải lại) → thương hiệu nhớ lần cuối, không nháy màu ShopTrack.
// Chưa tải xong thì giữ nguyên cái đang áp, không nháy về ShopTrack.
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { get } from '@/lib/api'
import { useAuthState } from '@/hooks/useAuthState'
import { useNppAdmin } from '@/lib/nppAdmin'
import { applyBrand, rememberedBrand, type BrandInfo } from '@/lib/brand'
import { isNppPath, useShopSettings } from '@/components/layout/shell-model'

/** Màn chưa đăng nhập dùng khung components/auth/AuthStage.tsx — luôn ShopTrack */
const AUTH_PATHS = new Set(['/login', '/signup', '/forgot-password', '/reset-password', '/accept-invite', '/2fa'])
export const isAuthScreen = (pathname: string) => AUTH_PATHS.has(pathname)

export function BrandSync() {
  const { pathname } = useLocation()
  const auth = useAuthState()
  const nppAdmin = useNppAdmin()
  const nppMode = isNppPath(pathname)
  const authScreen = isAuthScreen(pathname)
  // Nhân viên còn mật khẩu tạm (màn đổi mật khẩu) — máy chủ chặn mọi thứ trừ tài khoản của chính họ: chưa đọc đơn vị
  const shopId = auth.isAuthenticated && !nppMode && !auth.mustChangePassword ? auth.activeShopId : null
  const { data: shopSettings } = useShopSettings(shopId)
  const adminNppId = auth.isAuthenticated && nppMode ? nppAdmin.npp?.id ?? null : null
  const { data: adminBrand } = useQuery({
    queryKey: ['npp-brand', adminNppId],
    queryFn: () => get<{ brand: BrandInfo }>(`/public/brand/${encodeURIComponent(adminNppId!)}`).then((r) => r.brand),
    enabled: !!adminNppId,
    staleTime: 30_000,
  })

  useEffect(() => {
    if (authScreen) {
      applyBrand(null, { remember: false })
      return
    }
    if (!auth.isAuthenticated) {
      applyBrand(rememberedBrand(), { remember: false })
      return
    }
    if (nppMode) {
      if (adminBrand) applyBrand(adminBrand)
      return
    }
    // Đơn vị cũ chưa có brand trong phản hồi (api bản trước) → coi như chưa đặt
    if (shopSettings) applyBrand(shopSettings.brand ?? null)
  }, [authScreen, auth.isAuthenticated, nppMode, adminBrand, shopSettings])

  return null
}
